import React from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { HeartHandshake, Phone, MessageSquare, Globe, PenLine } from 'lucide-react';
import { openExternalUrl } from '@/hooks/useExternalLink';
import { useNavigate } from 'react-router-dom';
import * as analytics from '@/lib/analytics';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const SelfHarmSupportDialog: React.FC<Props> = ({ open, onOpenChange }) => {
  const navigate = useNavigate();
  const tap = (resource: string, fn: () => void) => {
    analytics.track('crisis_resource_tapped', { resource_type: resource, entry_point: 'self_harm_dialog' });
    fn();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md animate-in fade-in-0 zoom-in-95 duration-300">
        <DialogHeader>
          <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary">
            <HeartHandshake className="h-6 w-6" />
          </div>
          <DialogTitle className="text-center">We're here for you</DialogTitle>
          <DialogDescription className="text-center">
            Your vent mentions self-harm, so it can't be posted to the feed. This keeps you and others safe — you're not in trouble.
          </DialogDescription>
        </DialogHeader>

        <p className="text-sm text-muted-foreground text-center">
          If you'd still like to share, please revise it using safer words — how you're feeling, without describing self-harm.
        </p>

        <div className="space-y-2 pt-2">
          <p className="text-sm font-medium">If you need more support right now</p>
          <Button variant="outline" className="w-full justify-start" onClick={() => tap('988', () => (window.location.href = 'tel:988'))}>
            <Phone className="h-4 w-4 mr-2" /> Call or text 988 (Suicide & Crisis Lifeline)
          </Button>
          <Button variant="outline" className="w-full justify-start" onClick={() => tap('crisis_text_line', () => (window.location.href = 'sms:741741&body=HOME'))}>
            <MessageSquare className="h-4 w-4 mr-2" /> Text HOME to 741741
          </Button>
          <Button variant="outline" className="w-full justify-start" onClick={() => tap('international', () => openExternalUrl('https://findahelpline.com/'))}>
            <Globe className="h-4 w-4 mr-2" /> Find help outside the US
          </Button>
          <Button variant="ghost" className="w-full" onClick={() => { onOpenChange(false); navigate('/support'); }}>
            See all Get Help options
          </Button>
        </div>

        <Button className="w-full" onClick={() => onOpenChange(false)}>
          <PenLine className="h-4 w-4 mr-2" /> Revise my vent
        </Button>
      </DialogContent>
    </Dialog>
  );
};

export default SelfHarmSupportDialog;
